import { Check } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';

import type { PricingPlanBlock, PricingPlansSection } from '@/features/storefront/sections/schema';
import { pickedBlocks, shownBlocks } from '@/features/storefront/sections/schema';
import { serviceBookHref } from '@/features/storefront/sections/team';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';

/**
 * Two to four plans side by side: memberships, packages, diet or coaching plans.
 *
 * Each card is the merchant's own words - a name, the price as they write it
 * ("₹1,499", "Free"), what it includes one point per line - and a button that
 * goes where they sell it, a product or a page. Nothing here works out a
 * price or takes a payment, so a plan sold any way at all reads right.
 *
 * The featured plan is drawn in the primary colour with a solid button; the
 * rest wear an outline one, so the eye lands where the merchant wants it.
 * Left off the shop until a plan has a name; the customiser says so instead.
 */

export interface PricingPlansViewProps {
  section: PricingPlansSection;
  includeHidden?: boolean;
  /** The section's heading, drawn by the renderer so it can be typed into. */
  heading?: ReactNode;
  /** A link the way the renderer draws one: plain text in the customiser. */
  link: (href: string, className: string, children: ReactNode, style?: CSSProperties) => ReactNode;
  /** Wraps a card, so the customiser can select the block. */
  wrap?: (block: PricingPlanBlock, rendered: ReactNode) => ReactNode;
  /** Shown in the customiser while no plan has a name. */
  emptyHint?: ReactNode;
  /** Where a product lives in this shop, for a plan sold as one. */
  productHref?: ProductHref;
  /**
   * The button for a plan that sells a session package (`package_id`) - one
   * that buys it rather than links. Left off (the customiser, a sample shop),
   * such a plan's button is the link it would otherwise be.
   */
  packageButton?: PackageButton;
}

/** Draws a package plan's buy button, in the place and the style of its link. */
export type PackageButton = (
  block: PricingPlanBlock,
  className: string,
  children: ReactNode,
  style?: CSSProperties,
) => ReactNode;

/** The card's button: the package's buy button where there is one, else the link. */
function planButton(
  block: PricingPlanBlock,
  link: PricingPlansViewProps['link'],
  packageButton: PackageButton | undefined,
  productHref: ProductHref | undefined,
  className: string,
  style: CSSProperties,
): ReactNode {
  if (block.package_id && packageButton) {
    return packageButton(block, className, block.cta_label, style);
  }
  return link(planHref(block, productHref), className, block.cta_label, style);
}

/** The plans a shopper sees: shown ones with a name (every one, in the customiser). */
export function pricingPlans(section: PricingPlansSection, includeHidden = false): PricingPlanBlock[] {
  // Narrowed to the merchant's picks from the plan catalog - see `pickedBlocks`.
  return shownBlocks(pickedBlocks(section), includeHidden).filter(
    (block) => includeHidden || block.name.trim(),
  );
}

/**
 * Where a plan's button goes. A plan that books a service opens the booking
 * page on that service - the merchant's own link, else the Book page - so
 * "Book Essential" lands on Essential rather than on a form to search. A plan
 * sold as a product opens that product, where it is bought like any other.
 * Otherwise the merchant's link, else the catalog.
 */
export function planHref(block: PricingPlanBlock, productHref: ProductHref = defaultProductHref): string {
  if (block.service.trim()) return serviceBookHref(block.href, block.service);
  if (block.product.trim()) return productHref(block.product.trim());
  return block.href.trim() || '/products';
}

type ProductHref = (slug: string) => string;

function defaultProductHref(slug: string): string {
  return `/products/${encodeURIComponent(slug)}`;
}

/** What a plan includes, one point per line, blank lines dropped. */
export function planFeatures(block: PricingPlanBlock): string[] {
  return block.features
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

/**
 * A price as the merchant wrote it, in the pieces a large price is set in:
 * the currency sign, the whole amount and the paise or cents - "₹1,499.00"
 * is `₹`, `1,499` and `00`. Anything not shaped like an amount ("Free",
 * "On request") is `null`, and is shown as written.
 */
export function splitPrice(price: string): { currency: string; whole: string; cents: string; rest: string } | null {
  const match = price.trim().match(/^([^\d\s]*)\s*(\d[\d,]*)(?:\.(\d{1,2}))?\s*(.*)$/u);
  if (!match) return null;
  const [, currency = '', whole = '', cents = '', rest = ''] = match;
  return { currency, whole, cents, rest };
}

const CARD_STYLES: Record<PricingPlansSection['card_style'], CSSProperties> = {
  outlined: {
    backgroundColor: 'var(--storefront-background)',
    border: '1px solid color-mix(in srgb, var(--storefront-muted) 35%, transparent)',
  },
  filled: { backgroundColor: 'var(--storefront-surface)', border: '1px solid transparent' },
  shadow: {
    backgroundColor: 'var(--storefront-background)',
    border: '1px solid transparent',
    boxShadow: '0 12px 32px -12px rgb(0 0 0 / 0.25)',
  },
  tinted: {
    backgroundColor: 'color-mix(in srgb, var(--storefront-primary) 9%, var(--storefront-background))',
    border: '1.5px dashed color-mix(in srgb, var(--storefront-text) 45%, transparent)',
  },
};

/** The rule between a tinted card's points. */
const POINT_RULE = 'color-mix(in srgb, var(--storefront-text) 12%, transparent)';

/**
 * The tinted card: everything centred - the name large in the primary, a
 * small line in capitals under it, the price set big with its sign and paise
 * raised, the points ruled apart, a rounded button and a line under it.
 */
function TintedPlanCard({
  block,
  link,
  productHref,
  packageButton,
}: {
  block: PricingPlanBlock;
  link: PricingPlansViewProps['link'];
  productHref?: ProductHref;
  packageButton?: PackageButton;
}) {
  const features = planFeatures(block);
  const price = block.price.trim();
  const parts = price ? splitPrice(price) : null;
  return (
    <div
      data-plan={block.featured ? 'featured' : 'plan'}
      data-card-style="tinted"
      className="relative flex h-full flex-col items-center px-6 py-8 text-center"
      style={{
        ...CARD_STYLES.tinted,
        borderRadius: 'max(var(--storefront-radius), 1rem)',
        ...(block.featured ? { borderColor: 'var(--storefront-primary)', borderStyle: 'solid', borderWidth: 2 } : {}),
      }}
    >
      {block.badge.trim() ? (
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-0.5 text-xs font-semibold"
          style={{ backgroundColor: 'var(--storefront-primary)', color: 'var(--storefront-background)' }}
        >
          {block.badge}
        </span>
      ) : null}
      <h3 className="text-2xl sm:text-3xl" style={{ ...STOREFRONT_HEADING_STYLE, color: 'var(--storefront-primary)' }}>
        {block.name}
      </h3>
      {block.description.trim() ? (
        <p className="mt-2 text-xs uppercase tracking-[0.14em] opacity-80">{block.description}</p>
      ) : null}
      {price ? (
        <div className="mt-6" data-plan-price="">
          {parts ? (
            <p className="flex items-start justify-center leading-none" style={STOREFRONT_HEADING_STYLE}>
              {parts.currency ? <span className="mt-1.5 me-1 text-xl font-semibold">{parts.currency}</span> : null}
              <span className="text-6xl font-bold tracking-tight">{parts.whole}</span>
              {parts.cents ? <span className="mt-1 ms-0.5 text-xl font-semibold">.{parts.cents}</span> : null}
              {parts.rest ? <span className="ms-1.5 self-end text-base font-medium">{parts.rest}</span> : null}
            </p>
          ) : (
            <p className="text-4xl font-bold" style={STOREFRONT_HEADING_STYLE}>
              {price}
            </p>
          )}
          {block.period.trim() ? (
            <p className="mt-2 text-sm" style={{ color: 'var(--storefront-muted)' }}>
              {block.period}
            </p>
          ) : null}
        </div>
      ) : null}
      {features.length > 0 ? (
        <ul className="mt-6 w-full text-sm">
          {features.map((feature, index) => (
            <li
              key={index}
              className="flex items-center justify-center gap-2.5 border-b py-3"
              style={{ borderColor: POINT_RULE }}
            >
              <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {block.cta_label.trim() ? (
        <div className="mt-auto pt-6">
          {planButton(
            block,
            link,
            packageButton,
            productHref,
            'inline-flex h-11 items-center px-7 text-sm font-semibold',
            { ...STOREFRONT_BUTTON_STYLE, borderRadius: '9999px' },
          )}
        </div>
      ) : null}
      {block.note.trim() ? <p className="mt-4 text-sm font-medium">{block.note}</p> : null}
    </div>
  );
}

const OUTLINE_BUTTON: CSSProperties = {
  backgroundColor: 'transparent',
  color: 'var(--storefront-primary)',
  border: '1px solid var(--storefront-primary)',
  borderRadius: 'var(--storefront-radius)',
};

const GRID_COLUMNS: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
};

function PlanCard({
  block,
  cardStyle,
  link,
  productHref,
  packageButton,
}: {
  block: PricingPlanBlock;
  cardStyle: PricingPlansSection['card_style'];
  link: PricingPlansViewProps['link'];
  productHref?: ProductHref;
  packageButton?: PackageButton;
}) {
  if (cardStyle === 'tinted') {
    return <TintedPlanCard block={block} link={link} productHref={productHref} packageButton={packageButton} />;
  }
  const features = planFeatures(block);
  const style: CSSProperties = {
    ...CARD_STYLES[cardStyle],
    borderRadius: 'var(--storefront-radius)',
    ...(block.featured ? { border: '2px solid var(--storefront-primary)' } : {}),
  };
  return (
    <div
      data-plan={block.featured ? 'featured' : 'plan'}
      className={cn('relative flex h-full flex-col p-6', block.featured && 'lg:-my-3 lg:py-9')}
      style={style}
    >
      {block.badge.trim() ? (
        <span
          className="absolute -top-3 start-6 rounded-full px-3 py-0.5 text-xs font-semibold"
          style={{ backgroundColor: 'var(--storefront-primary)', color: 'var(--storefront-background)' }}
        >
          {block.badge}
        </span>
      ) : null}
      <h3 className="text-lg" style={STOREFRONT_HEADING_STYLE}>
        {block.name}
      </h3>
      {block.price.trim() ? (
        <p className="mt-3 flex items-baseline gap-1.5">
          <span className="text-3xl" style={STOREFRONT_HEADING_STYLE}>
            {block.price}
          </span>
          {block.period.trim() ? (
            <span className="text-sm" style={{ color: 'var(--storefront-muted)' }}>
              {block.period}
            </span>
          ) : null}
        </p>
      ) : null}
      {block.description.trim() ? (
        <p className="mt-3 text-sm" style={{ color: 'var(--storefront-muted)' }}>
          {block.description}
        </p>
      ) : null}
      {features.length > 0 ? (
        <ul className="mt-5 space-y-2 text-sm">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2">
              <Check
                className="mt-0.5 size-4 shrink-0"
                style={{ color: 'var(--storefront-primary)' }}
                aria-hidden
              />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {block.cta_label.trim() ? (
        <div className="mt-auto pt-6">
          {planButton(
            block,
            link,
            packageButton,
            productHref,
            'block h-11 w-full px-5 text-center text-sm font-semibold leading-[2.75rem]',
            block.featured ? STOREFRONT_BUTTON_STYLE : OUTLINE_BUTTON,
          )}
        </div>
      ) : null}
      {block.note.trim() ? (
        <p className="mt-3 text-center text-xs" style={{ color: 'var(--storefront-muted)' }}>
          {block.note}
        </p>
      ) : null}
    </div>
  );
}

export function PricingPlansView({
  section,
  includeHidden = false,
  heading,
  link,
  wrap,
  emptyHint,
  productHref,
  packageButton,
}: PricingPlansViewProps) {
  const plans = pricingPlans(section, includeHidden);
  if (plans.length === 0) return <>{emptyHint ?? null}</>;

  const cards = (
    <div
      className={cn(
        'grid items-stretch gap-6 sm:grid-cols-2',
        GRID_COLUMNS[Math.min(plans.length, section.layout === 'intro_left' ? 3 : 4)],
      )}
    >
      {plans.map((block) => {
        const card = (
          <PlanCard
            block={block}
            cardStyle={section.card_style}
            link={link}
            productHref={productHref}
            packageButton={packageButton}
          />
        );
        return <div key={block.id}>{wrap ? wrap(block, card) : card}</div>;
      })}
    </div>
  );

  if (section.layout === 'intro_left') {
    return (
      <div
        data-pricing-layout="intro_left"
        className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:items-center lg:gap-12"
      >
        <div>{heading}</div>
        {cards}
      </div>
    );
  }
  return (
    <div data-pricing-layout="cards">
      {heading}
      {cards}
    </div>
  );
}
